import React, { memo, useMemo, useState } from 'react';
import classNames from 'classnames';
import { DateTime } from 'luxon';

import { useWindowSize } from '../../../../hooks/useWindowSize';

import { STATUS } from '../../../../constants/statuses';
import { JOB_TASK_BUCKET_STATUS, JOB_TASK_CONTENT_TYPE } from '../../../../constants/jobs';

import ToolTip from '../../../../design-system/ToolTip/ToolTip';
import TimestampDisplay from '../../../../design-system/TimestampDisplay/TimestampDisplay';
import InboxIndicatorIcon from '../../../../design-system/Icons/InboxIndicatorIcon';
import MilestoneSidePanel from '../MilestoneSidePanel/MilestoneSidePanel';
import MilestoneBucketStatusIcon from '../../../../components/MilestoneBucketStatusIcon/MilestoneBucketStatusIcon';
import { SvgIcon } from '../../../../design-system';

const MilestoneCard = ({ milestone, bucketStatus, view, pageType }) => {
    const { label, created_at, updated_at, completed_at, participants, content } = milestone;

    const [isPanelOpened, setIsPanelOpened] = useState(false);

    const { width: screenWidth } = useWindowSize();

    const isDesktop = view === 'row' ? screenWidth >= 1024 : screenWidth > 1280;

    const timeToDisplay = { pending: created_at, processing: updated_at, completed: completed_at };
    const timeToDisplayLabel = {
        pending: 'Created At',
        processing: 'Updated At',
        completed: 'Completed At',
    };

    const formattedTime = DateTime.fromISO(timeToDisplay[bucketStatus]).toFormat(
        'MM/dd/yyyy - hh:mm a'
    );

    const participantClassName =
        'w-5 h-5 min-w-[20px] rounded-full bg-purple-500 flex items-center justify-center uppercase font-body text-body-regular-xs text-white';
    const dividingLineClassName = `relative pl-[7px] before:content-[''] before:w-[1px] before:h-[15px] before:bg-neutral-300 before:absolute before:top-1/2 before:transform before:translate-y-[-50%] before:left-0`;
    const participantsContainerClassName = classNames(
        'flex items-center gap-1',
        isDesktop && dividingLineClassName
    );

    const cardContainerClassName = classNames(
        'p-3 md:p-4 rounded-2 flex flex-col gap-3 md:gap-4 bg-white cursor-pointer',
        bucketStatus === JOB_TASK_BUCKET_STATUS.completed && 'opacity-60'
    );
    const containerClassName = classNames('flex overflow-hidden', {
        'flex-col-reverse gap-x-1.5 gap-y-2': !isDesktop,
        'flex-row items-center gap-x-1.5 gap-y-2': isDesktop,
    });

    const participantsToDisplay = participants?.slice(0, 3);
    const participantRest = participants?.length - 3;

    const isJobInReview = useMemo(() => {
        if (pageType !== 'project') return false;

        return content.some(
            (piece) =>
                piece.content_type === JOB_TASK_CONTENT_TYPE.agent &&
                piece?.data?.status === STATUS.in_review
        );
    }, [pageType, content]);

    return (
        <>
            <div className={cardContainerClassName} onClick={() => setIsPanelOpened(true)}>
                <div className="flex gap-2">
                    <MilestoneBucketStatusIcon bucketStatus={bucketStatus} />
                    <p className="font-body text-body-regular-m text-neutral-500 line-clamp-2">
                        {label}
                    </p>
                </div>
                <div className="flex items-end min-[1280px]:items-center gap-3 justify-between">
                    <div className={containerClassName}>
                        <TimestampDisplay
                            label={`${timeToDisplayLabel[bucketStatus]}:`}
                            timestamp={timeToDisplay[bucketStatus]}
                            variant="secondary"
                            dateTimeFormat="MM/dd/yyyy - hh:mm a"
                        />
                        {!!participants?.length && (
                            <div className={participantsContainerClassName}>
                                {participantsToDisplay.map(({ id, first_name, email }) => {
                                    const name = first_name || email;
                                    return (
                                        <div key={id} className={participantClassName}>
                                            {name?.[0]}
                                        </div>
                                    );
                                })}

                                {participantRest > 0 && (
                                    <p className="font-body text-body-regular-s text-neutral-300 whitespace-nowrap">
                                        + {participantRest}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    {isJobInReview && (
                        <ToolTip
                            text="Job in review"
                            position="top-right"
                            isShown={false}
                            shift="-8px"
                            useInternalHoverStateToShowToolTip={screenWidth >= 1024}
                        >
                            <SvgIcon color="#1F2125" icon={InboxIndicatorIcon} size="medium" />
                        </ToolTip>
                    )}
                </div>
            </div>

            {isPanelOpened && (
                <MilestoneSidePanel
                    milestone={milestone}
                    bucketStatus={bucketStatus}
                    pageType={pageType}
                    onClose={() => setIsPanelOpened(false)}
                />
            )}
        </>
    );
};

export default memo(MilestoneCard);
